/**
 * UI manipulation functions for Ragsmith Web UI
 */
import { applyRotation, rotateButtons, turnThumbnail } from './image-rotate.js';
import { state } from './state.js';
import {
    _buildCitationSection,
    openImageGallery,
    showToolsUsed,
    addMetaToMessage,
    addAnalyticsToMessage,
    showCitationLoading,
    updateCitations,
    addHeaderToMessage,
    showRoutingMatches,
    showImageSearchResults,
} from './ui-message-extras.js';
import {
    addLoadingStep,
    updateStepSubstep,
    updateLoadingStep,
    solidifyLoadingSteps,
    renderDataBlocks,
    showStepAccordion,
} from './ui-steps.js';
import {
    initDeleteModal,
    showDeleteConfirmModal,
    initRedoModal,
    showRedoModal,
    openAttachmentGallery,
} from './ui-modals.js';
import { mediaUrl } from './api.js';
import { escapeHtml } from './html-escape.js';

// DOM element references (initialized by initElements)
export const elements = {
    chatContainer: null,
    messageInput: null,
    sendBtn: null,
    agentSelect: null,
    modelSelect: null,
    statusEl: null,
    modeIndicators: null,
    loadingOverlay: null,
    loadingTitle: null,
    progressFill: null,
    progressText: null
};

// Sticky scroll state - tracks whether auto-scroll should happen
let isUserAtBottom = true;
const SCROLL_THRESHOLD = 50; // px from bottom to consider "at bottom"

/**
 * Format attachment size for display (e.g., "2.4 KB")
 */
function formatAttachmentSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Initialize DOM element references
 */
export function initElements() {
    elements.chatContainer = document.getElementById('chat-container');
    elements.messageInput = document.getElementById('message-input');
    elements.sendBtn = document.getElementById('send-btn');
    elements.agentSelect = document.getElementById('agent-select');
    elements.modelSelect = document.getElementById('model-select');
    elements.statusEl = document.getElementById('status');
    elements.modeIndicators = document.getElementById('mode-indicators');
    elements.loadingOverlay = document.getElementById('loading-overlay');
    elements.loadingTitle = document.getElementById('loading-title');
    elements.progressFill = document.getElementById('progress-fill');
    elements.progressText = document.getElementById('progress-text');

    // Track scroll position for sticky scroll behavior
    if (elements.chatContainer) {
        elements.chatContainer.addEventListener('scroll', handleScroll);
    }
}

/**
 * Handle scroll events to track if user is at bottom
 */
function handleScroll() {
    const container = elements.chatContainer;
    if (!container) return;

    // Check if user is at or near the bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    isUserAtBottom = distanceFromBottom <= SCROLL_THRESHOLD;
}

/**
 * Update status display
 */
export function updateStatus(text, isOnline) {
    if (!elements.statusEl) return;
    elements.statusEl.textContent = text;
    elements.statusEl.className = isOnline ? 'online' : 'offline';
}

/**
 * Update mode indicators in header
 */
export function updateModeIndicators(loadedAgent, loadedCollection, narratorMode, narratorChunkId) {
    // Chips are now styled purely via CSS classes on .mode-indicator.
    // Colors, dots, and shape live in styles.css — do NOT hard-code hex
    // inline here again; that historically fought responsive tweaks and
    // broke the design-token contract with the rest of the UI.
    // Chip content structure: a small uppercase label (AGENT / MODEL /
    // COLLECTION / NARRATOR) + hairline divider + accent-colored value.
    // Rendered by CSS on the .mode-indicator__label / __value spans.
    const chip = (cls, label, value, extras = '') =>
        `<span class="mode-indicator ${cls}" title="${value}"${extras}>` +
            `<span class="mode-indicator__label">${label}</span>` +
            `<span class="mode-indicator__value">${value}</span>` +
        `</span>`;

    let html = '';
    if (loadedAgent) {
        html += chip('agent', 'Agent', loadedAgent);
    }
    if (loadedCollection) {
        html += chip('collection', 'Collection', loadedCollection);
    }
    if (narratorMode) {
        if (state.narratorPaused) {
            html += chip('narrator paused', 'Narrator', 'Paused');
            elements.messageInput.placeholder = 'Type a message or /help for commands...';
        } else {
            // Count chunks narrated from chat messages that have narrator data
            let chunksRead = 0;
            if (elements.chatContainer) {
                elements.chatContainer.querySelectorAll('.message.assistant[data-narrator-chunk-id]').forEach(msg => {
                    chunksRead += parseInt(msg.dataset.narratorChunkCount || '1');
                });
            }
            const pos = chunksRead + 1;
            html += chip('narrator', 'Narrator', `Chunk ${pos} next`);
            const isVerbatim = state.agentSettings?.narrator_verbatim;
            elements.messageInput.placeholder = isVerbatim
                ? 'Press Enter to continue...'
                : 'Press Enter to continue, or type a question...';
        }
    } else {
        elements.messageInput.placeholder = 'Type a message or /help for commands...';
    }
    // Preserve the model badge (managed by menu.js updateHeaderStatus)
    const modelBadge = elements.modeIndicators.querySelector('.mode-indicator.model');
    elements.modeIndicators.innerHTML = html;
    if (modelBadge) elements.modeIndicators.appendChild(modelBadge);
}

/**
 * Update load button state (now delegated to menu)
 * This function exists for backwards compatibility
 */
export function updateLoadButton(agentValue, loadedAgent) {
    // Load button is now in the hamburger menu
    // menu.js handles its own updateMenuLoadButton
}

/**
 * Populate agent select dropdown
 */
export function populateAgents(agents) {
    // Clear and add default "No Agent" option
    elements.agentSelect.innerHTML = '<option value="">No Agent</option>';
    agents.forEach(agent => {
        const opt = document.createElement('option');
        opt.value = agent.name;
        opt.textContent = `${agent.name} (${agent.kb_count} KBs)`;
        elements.agentSelect.appendChild(opt);
    });
}

/**
 * Populate model select dropdown
 */
// Tier -> native vision support, populated from the tiers payload at startup. Used to
// gate promptless image submits (see chat.js). state.loadedModel is a tier name.
const _tierSupportsVision = {};

/**
 * Whether the currently loaded model natively supports vision (image input).
 * Reliable because populateModels runs at startup with the full tier list.
 */
export function loadedModelSupportsVision() {
    return !!_tierSupportsVision[state.loadedModel];
}

export function populateModels(tiers) {
    // Clear existing options
    elements.modelSelect.innerHTML = '';
    Object.entries(tiers).forEach(([tier, info]) => {
        _tierSupportsVision[tier] = !!info.supports_vision;
        if (info.installed) {
            const opt = document.createElement('option');
            opt.value = tier;
            // Capitalize tier name
            const tierDisplay = tier.charAt(0).toUpperCase() + tier.slice(1);
            // Clean up display name (remove suffix if present)
            let modelDisplay = info.display_name || tier;
            modelDisplay = modelDisplay.replace(/\s*\([^)]*\)\s*$/, '').trim();
            opt.textContent = `${tierDisplay} (${modelDisplay})`;
            // Store model info for getAssistantName()
            opt.dataset.tier = tierDisplay;
            opt.dataset.model = modelDisplay;
            elements.modelSelect.appendChild(opt);
        }
    });
}

/**
 * Show loading overlay for agent loading
 */
export function showLoadingOverlay(agentName) {
    elements.loadingTitle.textContent = `Loading ${agentName}`;
    elements.progressFill.style.width = '0%';
    elements.progressText.textContent = 'Initializing...';
    elements.loadingOverlay.classList.add('active');
}

/**
 * Update loading progress
 */
export function updateLoadingProgress(loaded, total, status) {
    const percent = Math.round((loaded / total) * 100);
    elements.progressFill.style.width = `${percent}%`;
    elements.progressText.textContent = status || `${loaded} / ${total} KBs (${percent}%)`;
}

/**
 * Hide loading overlay
 */
export function hideLoadingOverlay() {
    elements.loadingOverlay.classList.remove('active');
}

/**
 * Add a message to the chat container
 * @param {string} role - 'user', 'assistant', 'system', or 'command'
 * @param {string} content - Message content
 * @param {object} options - Optional settings: { header, meta, render, noScroll }
 *   - render: if true, render assistant messages as markdown
 *   - noScroll: if true, don't auto-scroll after adding
 */
/** "13:41" from seconds; empty when unknown. */
// The X inside a saved-narration pill (tts-player.js handles the click; the
// markup lives here too because this module draws the pill on a reload and
// cannot import from the player without a cycle).
const SAVED_X = '<span class="tts-saved-x" role="button" tabindex="0" title="Delete this audio" aria-label="Delete this audio">'
    + '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span>';

export function fmtNarrationLength(secs) {
    const s = Math.round(Number(secs) || 0);
    if (!s) return '';
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
    return h ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`;
}

/**
 * Whether a reply's play button shows. Audio already saved with the reply
 * plays from storage and needs nothing that can speak, so it is always
 * offered; making new audio needs the preference on and a voice to make it.
 */
export function playbackShown(messageEl) {
    if (messageEl?.dataset?.narrationMediaId) return true;
    return localStorage.getItem('ragsmith_playback') !== 'false' && state.ttsAvailable !== false;
}

/** Re-apply playbackShown to every play button on screen. */
export function applyPlaybackVisibility() {
    document.querySelectorAll('.message-action-btn.tts-btn').forEach(b => {
        b.style.display = playbackShown(b.closest('.message')) ? '' : 'none';
    });
}

/**
 * Keep a reply's saved-audio controls in step with whether it has saved
 * audio: the play button in the reply's top right corner exists only while
 * it does, and the footer button follows playbackShown.
 */
export function syncSavedAudio(messageEl) {
    if (!messageEl) return;
    const footer = messageEl.querySelector('.message-actions .tts-btn');
    if (footer) footer.style.display = playbackShown(messageEl) ? '' : 'none';
    let top = messageEl.querySelector('.message-saved-audio');
    if (!messageEl.dataset.narrationMediaId || !footer) {
        top?._mirror?.disconnect();
        top?.remove();
        return;
    }
    const len = fmtNarrationLength(messageEl.dataset.narrationSeconds);
    if (!top) {
        top = document.createElement('button');
        top.type = 'button';
        top.className = 'message-saved-audio';
        top.innerHTML = '<svg class="saved-audio-play" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6 4 20 12 6 20 6 4"/></svg>'
            + '<svg class="saved-audio-pause" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="5" y="4" width="5" height="16" rx="1"/><rect x="14" y="4" width="5" height="16" rx="1"/></svg>';
        // The player marks the footer button as it loads, plays and pauses;
        // this one shows the same state, so the two can never disagree.
        const mirror = () => {
            const playing = footer.classList.contains('playing');
            top.classList.toggle('playing', playing);
            top.classList.toggle('loading', footer.classList.contains('loading'));
            const secs = fmtNarrationLength(messageEl.dataset.narrationSeconds);
            top.title = playing ? 'Pause' : `Play saved audio${secs ? ` (${secs})` : ''}`;
            top.setAttribute('aria-label', top.title);
        };
        top._mirror = new MutationObserver(mirror);
        top._mirror.observe(footer, { attributes: true, attributeFilter: ['class'] });
        // The footer button owns playback (and its pause/resume state), so
        // this one presses it rather than starting a second path.
        top.addEventListener('click', () => messageEl.querySelector('.message-actions .tts-btn')?.click());
        // In the header row when the reply has one, so it sits above the
        // rule; otherwise floated in the corner with the text around it.
        const header = messageEl.querySelector(':scope > .message-header');
        if (header) header.appendChild(top);
        else messageEl.prepend(top);
    }
    const playing = footer.classList.contains('playing');
    top.classList.toggle('playing', playing);
    top.title = playing ? 'Pause' : `Play saved audio${len ? ` (${len})` : ''}`;
    top.setAttribute('aria-label', top.title);
}

export function addMessage(role, content, options = null) {
    const div = document.createElement('div');
    div.className = `message ${role}`;

    // Dictation messages: use assistant styling but with a Dictation badge.
    // The user's voice IS the response, so there's no companion user message.
    if (options?.isDictation) {
        div.classList.add('assistant', 'dictation-message');
        if (options.messageId) div.dataset.messageId = options.messageId;
        div.dataset.isDictation = 'true';

        const headerDiv = document.createElement('div');
        headerDiv.className = 'message-header';
        headerDiv.innerHTML = '<span class="dictation-badge">Dictation</span>';
        div.appendChild(headerDiv);
    }

    // Add header (agent name) for assistant messages
    if (options?.header && !options?.isDictation) {
        const headerDiv = document.createElement('div');
        headerDiv.className = 'message-header';
        headerDiv.textContent = options.header;
        div.appendChild(headerDiv);
    }

    // Add attachment chips if provided (before message content)
    if (options?.attachments && options.attachments.length > 0) {
        // Remembered as a JS property (not a data attribute — image content
        // is base64 and would bloat the DOM): the redo modal reads this to
        // resubmit the same media without re-uploading or re-recording.
        div._attachments = options.attachments;
        const attachmentsDiv = document.createElement('div');
        attachmentsDiv.className = 'message-attachments';

        options.attachments.forEach(att => {
            if (att.type === 'audio' && att.media_id) {
                // A saved narration: not a chip, a property of the message.
                // The play button plays this copy instead of re-rendering.
                div.dataset.narrationMediaId = att.media_id;
                // Length: recorded at save time; older saves fall back to
                // the MP3's constant bitrate (128 kbps), which is exact enough.
                const secs = Number(att.meta?.duration) || (att.size ? att.size * 8 / 128000 : 0);
                if (secs) div.dataset.narrationSeconds = String(Math.round(secs));
                return;
            }
            if (att.type === 'video' && att.content) {
                // Playable clip. Deliberately NOT .msg-attachment-thumbnail —
                // the image lightbox collects that selector for arrow-key
                // navigation and a <video> in that set breaks it — and no
                // openAttachmentViewer, which dumps att.content into a <pre>
                // and would show a raw media id.
                const wrap = document.createElement('div');
                wrap.className = 'attachment-chip video-chip';
                wrap.dataset.attachmentId = att.id;

                const vid = document.createElement('video');
                vid.className = 'msg-attachment-video';
                vid.controls = true;
                vid.preload = 'metadata';
                vid.playsInline = true;
                vid.poster = mediaUrl(att.content, 'poster');
                vid.src = mediaUrl(att.content, 'stream');
                wrap.appendChild(vid);

                // A clip whose holder is unavailable answers 503 with a
                // reason ("LilRaspy is switched off right now"). The bare
                // <video> renders that as greyed-out controls on a working
                // poster — which reads as a broken player, not a resting
                // file. Say it: grey the whole chip and put the state, and
                // the reason, where the eye lands.
                //
                // Probed at render with a one-byte ranged fetch rather than
                // by listening for the element's error event: browsers are
                // inconsistent about firing it for an HTTP error on a
                // preload=metadata source, and the first cut of this —
                // error-event only — showed nothing on a chip whose stream
                // was answering 503 on every request.
                const markUnavailable = (reason) => {
                    if (wrap.querySelector('.video-unavailable')) return;
                    wrap.classList.add('video-chip-unavailable');
                    const overlay = document.createElement('div');
                    overlay.className = 'video-unavailable';
                    overlay.innerHTML = `<span class="video-unavailable-title">Unavailable</span>` +
                        (reason ? `<span class="video-unavailable-why">${escapeHtml(reason)}</span>` : '');
                    wrap.appendChild(overlay);
                };
                (async () => {
                    try {
                        const r = await fetch(vid.src, { headers: { Range: 'bytes=0-0' } });
                        if (r.status === 503) {
                            let reason = '';
                            try { reason = (await r.json()).detail || ''; } catch { /* non-JSON 503 */ }
                            markUnavailable(reason);
                        }
                    } catch { /* network down: the element's own error UI applies */ }
                })();

                const caption = document.createElement('div');
                caption.className = 'video-caption';
                caption.innerHTML = `<span class="attachment-name">${escapeHtml(att.name || 'video')}</span>`;
                // What the model actually read. Without this there is no way
                // to tell a bad frame sample from a bad answer.
                if (att.analysis) {
                    const link = document.createElement('button');
                    link.className = 'video-analysis-link';
                    link.textContent = 'what the model saw';
                    link.addEventListener('click', () => {
                        if (window.openVideoAnalysis) window.openVideoAnalysis(att);
                    });
                    caption.appendChild(link);
                }
                wrap.appendChild(caption);
                attachmentsDiv.appendChild(wrap);
            } else if (att.type === 'image' && (att.content || att.media_id)) {
                // Image attachment - render clickable thumbnail.
                //
                // The bytes travel inline only on the browser that uploaded
                // them. Once saved, the message carries a media id and the
                // bytes live on the media rails (the container's storage
                // worker, or the front box), so every other browser — and
                // this one after a reload — has to fetch them by id. Without
                // this branch such an image fell through to the text chip,
                // whose viewer showed an empty box with the file's name on it.
                const chip = document.createElement('div');
                chip.className = 'attachment-chip image-chip clickable';
                chip.dataset.attachmentId = att.id;

                const img = document.createElement('img');
                img.className = 'msg-attachment-thumbnail';
                img.src = att.content
                    ? `data:${att.media_type || 'image/jpeg'};base64,${att.content}`
                    : mediaUrl(att.media_id, 'stream');
                img.alt = att.name || 'Image';
                // The thumbnail is the page's source of truth for a picture's
                // turn: the full view reads these rather than keeping its own.
                img.dataset.attachmentId = att.id || '';
                img.dataset.messageId = options.messageId ? String(options.messageId) : '';
                img.dataset.rotation = String(att.meta?.rotation || 0);
                const frame = document.createElement('div');
                frame.className = 'img-rotatable';
                frame.appendChild(img);
                if (!state.capsuleMode) {
                    frame.appendChild(rotateButtons((dir) => turnThumbnail(img, dir)));
                }
                chip.appendChild(frame);
                if (att.meta?.rotation) applyRotation(img, att.meta.rotation, 512);

                // Click to open gallery with all conversation images
                chip.addEventListener('click', () => {
                    openAttachmentGallery(img);
                });
                attachmentsDiv.appendChild(chip);
            } else {
                // Text/code attachment - existing chip
                const chip = document.createElement('div');
                chip.className = 'attachment-chip clickable';
                chip.dataset.attachmentId = att.id;
                chip.innerHTML = `
                    <span class="attachment-icon">${att.type === 'code' ? '📄' : '📝'}</span>
                    <span class="attachment-name">${escapeHtml(att.name)}</span>
                    <span class="attachment-size">(${formatAttachmentSize(att.size)})</span>
                `;
                // Click to view attachment content
                chip.addEventListener('click', () => {
                    if (window.openAttachmentViewer) {
                        window.openAttachmentViewer(att);
                    }
                });
                attachmentsDiv.appendChild(chip);
            }
        });

        if (attachmentsDiv.childElementCount) div.appendChild(attachmentsDiv);
    }

    // Detect doc download link pattern in content (persisted from doc_create post-processing)
    let docLinkData = null;
    const docLinkPattern = /\n\n---\n\[([^\]]+)\]\((\/documents\/[^)]+)\)$/;
    const docMatch = role === 'assistant' ? content.match(docLinkPattern) : null;
    if (docMatch) {
        docLinkData = { filename: docMatch[1], url: docMatch[2] };
        content = content.replace(docLinkPattern, '');
    }

    // Add content wrapper
    const contentDiv = document.createElement('div');
    contentDiv.className = 'message-content';

    // Document-bubble user message — rendered as a filename chip plus a
    // click-to-expand block showing the extracted text. The raw content
    // (the full document text) stays accessible to the user via the
    // toggle but isn't dumped into the bubble body where it would bloat
    // the chat. Used by the "Ingest Document" feature.
    if (options?.isDocument && role === 'user') {
        const filename = options.filename || 'document';

        const chip = document.createElement('div');
        chip.className = 'document-chip';
        chip.innerHTML = `
            <span class="document-chip-icon">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                </svg>
            </span>
            <span class="document-chip-name"></span>
        `;
        chip.querySelector('.document-chip-name').textContent = filename;
        contentDiv.appendChild(chip);

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'document-toggle';
        toggle.innerHTML = `
            <svg class="document-toggle-chevron" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"/>
            </svg>
            <span class="document-toggle-label">Show document text</span>
        `;

        const body = document.createElement('div');
        body.className = 'document-body';
        body.textContent = content;

        toggle.addEventListener('click', () => {
            const open = toggle.classList.toggle('open');
            toggle.querySelector('.document-toggle-label').textContent =
                open ? 'Hide document text' : 'Show document text';
        });

        contentDiv.appendChild(toggle);
        contentDiv.appendChild(body);
    } else if (options?.render && role === 'assistant' && typeof marked !== 'undefined' && typeof DOMPurify !== 'undefined') {
        // Render markdown for assistant messages if requested
        contentDiv.innerHTML = assistantMarkdownToHtml(content);
        _addCodeCopyButtons(contentDiv);
        _makeGeneratedImagesClickable(contentDiv);
    } else {
        contentDiv.textContent = content;
    }
    div.appendChild(contentDiv);

    // Add meta info if provided
    if (options?.meta) {
        const metaDiv = document.createElement('div');
        metaDiv.className = 'meta';
        metaDiv.textContent = options.meta;
        div.appendChild(metaDiv);
    }

    // Add citations if provided (for restored messages)
    if (options?.citations && options.citations.length > 0) {
        div.appendChild(_buildCitationSection(options.citations));
    }

    // Dictation footer: Playback / Edit / Delete (no Redo since there's no LLM cycle)
    if (options?.isDictation && !options?.inProgress) {
        const actionBar = document.createElement('div');
        actionBar.className = 'message-actions';
        // Preference AND capability: a play button with no voice worker behind
        // it fails several seconds after the click, which reads as a broken
        // feature rather than an unavailable one.
        const playbackOn = localStorage.getItem('ragsmith_playback') !== 'false'
            && state.ttsAvailable !== false;
        const extractingHtml = options.extracting ? `
            <div class="extraction-status">
                <div class="mini-spinner"></div>
                <span>Extracting facts...</span>
            </div>
        ` : '';
        actionBar.innerHTML = `
            ${extractingHtml}
            <button class="message-action-btn tts-btn" title="Play audio" style="display:${playbackOn ? '' : 'none'}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                </svg>
            </button>
            <button class="message-action-btn edit-btn ready" title="Edit dictation">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
            </button>
            <button class="message-action-btn delete-btn ready" title="Delete dictation">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
            </button>
        `;
        // TTS playback
        actionBar.querySelector('.tts-btn')?.addEventListener('click', (e) => {
            const btn = e.currentTarget;
            const text = div.querySelector('.message-content')?.innerText?.trim();
            if (text) {
                window.dispatchEvent(new CustomEvent('tts-play', {
                    detail: { text, messageEl: div, button: btn }
                }));
            }
        });
        // Delete (uses existing confirm modal)
        actionBar.querySelector('.delete-btn')?.addEventListener('click', () => {
            if (window.showDeleteConfirmModal && options.messageId) {
                window.showDeleteConfirmModal(options.messageId, div);
            }
        });
        // Edit — replace content with a textarea + Save/Cancel
        actionBar.querySelector('.edit-btn')?.addEventListener('click', () => {
            startDictationEdit(div, options.messageId);
        });
        div.appendChild(actionBar);
        elements.chatContainer.appendChild(div);
        // Re-evaluate "last message" visibility (delete + edit gated to last)
        updateRedoButtonVisibility();
        if (!options?.noScroll) scrollNewMessageIntoView();
        return div;
    }

    // Add action bar for assistant messages (but not error messages - they have their own retry/delete buttons)
    // - messageId present: loaded from history, show delete button ready
    // - extracting: true: new message, show spinner, poll for completion
    const isErrorMessage = content.startsWith('⚠️ Error') || content.startsWith('Error:') || content.includes('Response interrupted:');
    if (role === 'assistant' && (options?.messageId || options?.extracting || options?.cancelled || options?.playbackOnly) && !isErrorMessage) {
        const actionBar = document.createElement('div');
        actionBar.className = 'message-actions';

        // Redo button SVG (refresh/redo icon)
        const redoButtonHtml = `
            <button class="message-action-btn redo-btn" title="Edit and resubmit this prompt">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                    <path d="M3 3v5h5"/>
                    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
                    <path d="M21 21v-5h-5"/>
                </svg>
            </button>
        `;

        // Edit button SVG (pencil) — opens the response in a rich-text editor.
        // Unlike redo, this is offered on ANY assistant message: the point is
        // to correct a mistake wherever it sits, not only in the last turn.
        const editButtonHtml = `
            <button class="message-action-btn msg-edit-btn" title="Edit this response">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"/>
                </svg>
            </button>
        `;

        // Copy: puts the response on the clipboard as rich text AND plain, so
        // it pastes formatted into Word and clean into a terminal.
        const copyButtonHtml = `
            <button class="message-action-btn msg-copy-btn" title="Copy this response">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
            </button>
        `;

        // Export: a small menu of file formats, built on the server.
        const exportButtonHtml = `
            <button class="message-action-btn msg-export-btn" title="Export this response">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
            </button>
        `;

        // TTS play button SVG (speaker icon) — shown when playback is enabled
        // Preference AND capability: a play button with no voice worker behind
        // it fails several seconds after the click, which reads as a broken
        // feature rather than an unavailable one.
        const playbackOn = playbackShown(div);
        const savedNarration = !!div.dataset.narrationMediaId;
        const savedLen = savedNarration ? fmtNarrationLength(div.dataset.narrationSeconds) : '';
        const ttsButtonHtml = `
            <button class="message-action-btn tts-btn${savedNarration ? ' has-saved' : ''}" title="${savedNarration ? `Play saved narration${savedLen ? ` (${savedLen})` : ''}` : 'Play audio'}" style="display:${playbackOn ? '' : 'none'}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                </svg>${savedLen ? `<span class="tts-saved-len">${savedLen}</span>` : ''}${savedNarration ? SAVED_X : ''}
            </button>
        `;

        // Delete button SVG
        const deleteButtonHtml = `
            <button class="message-action-btn delete-btn" title="Delete this response">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
            </button>
        `;

        if (options?.playbackOnly) {
            // Playback-only footer (e.g. original capsule messages)
            actionBar.innerHTML = ttsButtonHtml;
        } else if (options?.cancelled) {
            // Cancelled search — show TTS + delete (DOM-only) immediately, no spinner
            const showDelete = options.hasSession;
            actionBar.innerHTML = `
                ${ttsButtonHtml}
                ${redoButtonHtml.replace('redo-btn"', 'redo-btn ready"')}
                ${showDelete ? deleteButtonHtml.replace('delete-btn"', 'delete-btn ready"') : ''}
            `;
            // Redo opens the modal for editing + resubmit
            actionBar.querySelector('.redo-btn')?.addEventListener('click', () => {
                if (window.showRedoModal) {
                    window.showRedoModal(null, div);
                }
            });
            if (showDelete) {
                actionBar.querySelector('.delete-btn')?.addEventListener('click', () => {
                    // DOM-only removal — no DB record exists for cancelled responses
                    const userMsg = div.previousElementSibling;
                    if (userMsg?.classList?.contains('user-message')) userMsg.remove();
                    div.remove();
                });
            }
        } else if (options?.extracting) {
            // New message - show extraction spinner, redo hidden until ready
            const statusLabel = options?.running ? 'Running...' : 'Extracting facts...';
            // Verbatim narration extracts nothing: the chunk comes out of the
            // knowledge base and the server returns before it ever schedules
            // extraction, so "Extracting facts..." describes work that is not
            // happening. Only the STATUS ROW goes; this branch also builds the
            // buttons, and suppressing the whole thing left a message with no
            // footer until the page was refreshed. "Running..." still shows,
            // because a tool loop is real work whatever the mode.
            const verbatimNarration = state.narratorMode
                && !state.narratorPaused
                && state.agentSettings?.narrator_verbatim;
            const statusHtml = (options?.running || !verbatimNarration) ? `
                <div class="extraction-status">
                    <div class="mini-spinner"></div>
                    <span>${statusLabel}</span>
                </div>
            ` : '';
            actionBar.innerHTML = `
                ${statusHtml}
                ${ttsButtonHtml}
                ${redoButtonHtml}
                ${editButtonHtml}
                ${copyButtonHtml}
                ${exportButtonHtml}
                ${deleteButtonHtml}
            `;
        } else {
            // Loaded from history - show buttons ready
            div.dataset.messageId = options.messageId;
            // Store narrator data if present (for delete revert functionality)
            if (options.narratorChunkId) {
                div.dataset.narratorChunkId = options.narratorChunkId;
                div.dataset.narratorBook = options.narratorBook || '';
                div.dataset.narratorSection = options.narratorSection || '';
                div.dataset.narratorTotalChunks = options.narratorTotalChunks || '';
                div.dataset.narratorKbPath = options.narratorKbPath || '';
                div.dataset.narratorChunkCount = options.narratorChunkCount || '1';
            }
            actionBar.innerHTML = `
                ${ttsButtonHtml}
                ${redoButtonHtml.replace('redo-btn"', 'redo-btn ready"')}
                ${editButtonHtml.replace('msg-edit-btn"', 'msg-edit-btn ready"')}
                ${copyButtonHtml.replace('msg-copy-btn"', 'msg-copy-btn ready"')}
                ${exportButtonHtml.replace('msg-export-btn"', 'msg-export-btn ready"')}
                ${deleteButtonHtml.replace('delete-btn"', 'delete-btn ready"')}
            `;
            actionBar.querySelector('.msg-edit-btn')?.addEventListener('click', () => {
                window.openMessageEditor?.(div, options.messageId);
            });
            wireCopyAndExport(actionBar, div, options.messageId);
            actionBar.querySelector('.delete-btn').addEventListener('click', () => {
                if (window.showDeleteConfirmModal) {
                    window.showDeleteConfirmModal(options.messageId, div);
                }
            });
            actionBar.querySelector('.redo-btn').addEventListener('click', () => {
                if (window.showRedoModal) {
                    window.showRedoModal(options.messageId, div);
                }
            });
        }

        // TTS play button click — dispatches custom event for tts-player.js
        const ttsBtn = actionBar.querySelector('.tts-btn');
        if (ttsBtn) {
            ttsBtn.addEventListener('click', () => {
                const text = div.querySelector('.message-content')?.innerText?.trim();
                if (text) {
                    window.dispatchEvent(new CustomEvent('tts-play', {
                        detail: { text, messageEl: div, button: ttsBtn }
                    }));
                }
            });
        }

        div.appendChild(actionBar);
        syncSavedAudio(div);

        // Add tool badges if tools were used (for restored messages)
        if (options?.toolsUsed && (Array.isArray(options.toolsUsed) ? options.toolsUsed.length > 0 : Object.keys(options.toolsUsed).length > 0)) {
            showToolsUsed(div, options.toolsUsed);
        }

        // Add step accordion for restored messages
        if (options?.stepLog && options.stepLog.length > 0) {
            showStepAccordion(div, options.stepLog);

            // Restore data blocks from step log
            const dataBlocks = options.stepLog
                .filter(e => e.raw_output)
                .map(e => ({ tool_label: e.tool_label || e.tool, args_summary: e.args_summary || '', raw_output: e.raw_output }));
            if (dataBlocks.length > 0) {
                renderDataBlocks(div, dataBlocks);
            }
        }
    }

    // Add doc download badge if detected (restored from DB)
    if (docLinkData) {
        const actionBar = div.querySelector('.message-actions');
        const linkEl = document.createElement('div');
        linkEl.className = 'doc-download-bar';
        const ext = docLinkData.filename.split('.').pop()?.toUpperCase() || 'FILE';
        const btn = document.createElement('button');
        btn.className = 'doc-download-link';
        btn.innerHTML = `<span class="doc-download-icon">&#8681;</span> ${escapeHtml(docLinkData.filename)}<span class="doc-download-badge">${escapeHtml(ext)}</span>`;
        btn.addEventListener('click', async () => {
            try {
                const { getAuthToken } = await import('./auth.js');
                const headers = {};
                const token = getAuthToken();
                if (token) headers['X-Auth-Token'] = token;
                const res = await fetch(docLinkData.url, { headers });
                if (!res.ok) throw new Error(res.status);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = docLinkData.filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch (e) {
                // Document expired or unavailable — show expired state
                btn.classList.add('expired');
                btn.innerHTML = `<span class="doc-download-icon">&#8681;</span> ${escapeHtml(docLinkData.filename)}<span class="doc-download-badge expired-badge">EXPIRED</span>`;
                btn.disabled = true;
            }
        });
        linkEl.appendChild(btn);
        if (actionBar) actionBar.parentNode.insertBefore(linkEl, actionBar);
        else div.appendChild(linkEl);
    }

    elements.chatContainer.appendChild(div);

    // Update redo button visibility (only last assistant message should show it)
    updateRedoButtonVisibility();

    // Update narrator mode delete restrictions
    updateNarratorDeleteButtons();

    // Bring new messages into view (resets sticky scroll state)
    if (!options?.noScroll) {
        scrollNewMessageIntoView();
    }
    return div;
}

/**
 * Wire copy and export on one message's footer.
 *
 * Copy needs no message id — it reads the rendered bubble — but export asks
 * the server to build the file, so it stays dark until the message is saved.
 */
function wireCopyAndExport(actionBar, messageEl, messageId) {
    const copyBtn = actionBar.querySelector('.msg-copy-btn');
    if (copyBtn && !copyBtn.dataset.wired) {
        copyBtn.dataset.wired = '1';
        copyBtn.classList.add('ready');
        copyBtn.addEventListener('click', () => window.copyMessage?.(messageEl, copyBtn));
    }
    const exportBtn = actionBar.querySelector('.msg-export-btn');
    if (exportBtn && messageId && !exportBtn.dataset.wired) {
        exportBtn.dataset.wired = '1';
        exportBtn.classList.add('ready');
        exportBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            window.openExportMenu?.(messageEl, messageId, exportBtn);
        });
    }
}

/**
 * Update redo button visibility - only the last assistant message should show it
 */
export function updateRedoButtonVisibility() {
    if (!elements.chatContainer) return;

    const assistantMessages = elements.chatContainer.querySelectorAll('.message.assistant');
    assistantMessages.forEach((msg, index) => {
        const isLast = index === assistantMessages.length - 1;
        const redoBtn = msg.querySelector('.redo-btn');
        if (redoBtn) {
            redoBtn.classList.toggle('hidden', !isLast);
        }
        // Dictation messages: gate delete + edit to last-only (matches normal
        // chat UX where you can only undo the most recent action). Edit is
        // also gated since editing a non-last dictation would invalidate any
        // assistant responses that referenced it.
        if (msg.classList.contains('dictation-message')) {
            const deleteBtn = msg.querySelector('.delete-btn');
            const editBtn = msg.querySelector('.edit-btn');
            if (deleteBtn) deleteBtn.classList.toggle('hidden', !isLast);
            if (editBtn) editBtn.classList.toggle('hidden', !isLast);
        }
    });
}

/**
 * Add a system message
 */
export function addSystemMessage(content) {
    return addMessage('system', content);
}

/**
 * Attach a delete button to an orphaned user-message element.
 *
 * An orphan is a user message with no assistant response — usually
 * because the user cancelled before generation completed. The button
 * deletes the message from the DB via the standalone-user-delete
 * branch of /sessions/{sid}/messages/{mid} and removes the element
 * from the DOM.
 *
 * The button is styled subtly so it doesn't draw attention away from
 * the conversation flow — just a small "Delete" link in the corner.
 */
export function attachOrphanDeleteButton(messageEl, sessionId, messageId, onDeleted) {
    if (!messageEl || !sessionId || !messageId) return;
    // Don't double-attach if loaded multiple times
    if (messageEl.querySelector('.orphan-delete-btn')) return;
    messageEl.classList.add('orphan-message');

    const bar = document.createElement('div');
    bar.className = 'orphan-actions';
    bar.innerHTML = `
        <span class="orphan-tag">No response</span>
        <button class="orphan-delete-btn" title="Delete this orphaned prompt">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
            </svg>
            Delete
        </button>
    `;
    messageEl.appendChild(bar);

    bar.querySelector('.orphan-delete-btn').addEventListener('click', async () => {
        const btn = bar.querySelector('.orphan-delete-btn');
        btn.disabled = true;
        btn.textContent = 'Deleting…';
        try {
            // getHeaders(), not a hand-built header: it carries whichever
            // credential this tab actually has — the admin token, or the
            // container/capsule token. This path added only the admin
            // token, so in a container tab the DELETE went out with no
            // credential at all and the server correctly answered 401,
            // which surfaced as "Delete failed — retry?" on a message the
            // user had every right to remove.
            const { getHeaders } = await import('./api.js');
            const res = await fetch(`/sessions/${sessionId}/messages/${messageId}`, {
                method: 'DELETE',
                headers: getHeaders(true),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            messageEl.remove();
            if (onDeleted) onDeleted();
        } catch (err) {
            console.error('[Orphan delete] failed:', err);
            btn.disabled = false;
            btn.textContent = 'Delete failed — retry?';
        }
    });
}

/**
 * Display collection info header at top of chat
 */
export function showCollectionHeader(title, description) {
    if (!title) return;

    // Remove any existing collection header
    const existing = document.getElementById('collection-header');
    if (existing) existing.remove();

    const header = document.createElement('div');
    header.id = 'collection-header';
    header.className = 'collection-header';

    header.innerHTML = `
        <div class="collection-header-icon">📚</div>
        <div class="collection-header-content">
            <div class="collection-header-title">${title}</div>
            ${description ? `<div class="collection-header-description">${description}</div>` : ''}
        </div>
    `;

    // Insert at the top of chat container
    elements.chatContainer.insertBefore(header, elements.chatContainer.firstChild);
}

/**
 * Remove collection header
 */
export function hideCollectionHeader() {
    const existing = document.getElementById('collection-header');
    if (existing) existing.remove();
}

/**
 * Add command output message
 */
export function addCommandOutput(content) {
    return addMessage('command', content);
}

/**
 * Show loading indicator in chat (with hidden stop button for cancel support)
 */
export function showLoading(afterElement = null) {
    const div = document.createElement('div');
    div.className = 'message assistant loading';
    div.id = 'loading-indicator';
    div.innerHTML = `
        <div class="loading-header">
            <div class="spinner"></div>
            <span class="loading-status">Processing...</span>
            <button class="loading-expand-btn" title="Show steps">
                <svg class="loading-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"/>
                </svg>
                <span class="loading-step-count"></span>
            </button>
        </div>
        <div class="loading-steps">
            <div class="step-grid"></div>
        </div>`;

    // Wire expand/collapse toggle
    const expandBtn = div.querySelector('.loading-expand-btn');
    expandBtn.addEventListener('click', () => {
        div.classList.toggle('expanded');
    });

    // Insert after a specific element (e.g. assistantDiv) or append to end
    if (afterElement && afterElement.parentNode) {
        afterElement.parentNode.insertBefore(div, afterElement.nextSibling);
    } else {
        elements.chatContainer.appendChild(div);
    }
    forceScrollToBottom();
}

/**
 * No-op stub. The dedicated stop button in the loading spinner has been
 * replaced by the send button itself transforming into a stop button
 * (`.stopping` class on #send-btn). Kept as a stub so existing call sites
 * in chat.js don't have to be touched in the same diff.
 */
export function showStopButton(_visible) {
    // intentionally empty
}

/**
 * Convert an assistant message bubble created with `extracting: true` into
 * the cancelled state: tear down the spinning extraction status and wire
 * the redo + delete handlers. Used when /chat/cancel landed mid-stream,
 * so the bubble was created in the "we're working" state and never got
 * the buttons wired by the normal extraction-complete path
 * (updateExtractionStatus).
 *
 * Cancelled responses ARE saved to the DB (the tool loop yields a result
 * event whether it finished naturally or was cancelled, and the wrapper
 * adds the message either way). When a messageId is available we wire the
 * normal redo/delete flow (modal with messageId; DB-backed delete). When
 * not, we fall back to a DOM-only delete.
 */
export function markMessageCancelled(assistantDiv, hasSession, messageId = null) {
    if (!assistantDiv) return;
    const actionBar = assistantDiv.querySelector('.message-actions');
    if (!actionBar) return;
    // Drop the spinning "Extracting facts… / Running…" pill
    actionBar.querySelector('.extraction-status')?.remove();

    if (messageId) {
        assistantDiv.dataset.messageId = messageId;
    }

    // Wire redo — opens the modal for editing + resubmit
    const redoBtn = actionBar.querySelector('.redo-btn');
    if (redoBtn) {
        redoBtn.classList.add('ready');
        redoBtn.addEventListener('click', () => {
            if (window.showRedoModal) {
                window.showRedoModal(messageId || null, assistantDiv);
            }
        });
    }

    // Wire delete — DB-backed when messageId known, DOM-only otherwise.
    const deleteBtn = actionBar.querySelector('.delete-btn');
    if (deleteBtn) {
        if (messageId) {
            deleteBtn.classList.add('ready');
            deleteBtn.addEventListener('click', () => {
                if (window.showDeleteConfirmModal) {
                    window.showDeleteConfirmModal(messageId, assistantDiv);
                }
            });
        } else if (hasSession) {
            deleteBtn.classList.add('ready');
            deleteBtn.addEventListener('click', () => {
                // Class token is 'user' (div.className = `message ${role}`);
                // the old 'user-message' check never matched, so the paired
                // prompt was left behind. Never treat a dictation as the
                // pair prompt (they're user-role rows that delete alone).
                const userMsg = assistantDiv.previousElementSibling;
                if (
                    userMsg?.classList?.contains('user') &&
                    !userMsg.classList.contains('dictation-message')
                ) {
                    userMsg.remove();
                }
                assistantDiv.remove();
            });
        } else {
            deleteBtn.remove();
        }
    }
}

/**
 * Render a fact-ingest chip in the chat flow.
 *
 * The chip is the *only* visual artifact for the "Extract facts only"
 * ingest mode — no user bubble, no assistant bubble. It shows: 📄
 * filename · N facts · [expand] · [delete]. Clicking the row toggles
 * the fact list (key: value lines). Clicking delete shows a confirm
 * modal and, on confirm, calls DELETE /sessions/{sid}/fact-ingest/{cid}
 * which removes both the chip message and the linked facts (matched by
 * source_marker, so only THIS ingest's facts go).
 *
 * Used both for fresh ingests (called from ingest.js with the server's
 * /document/extract response) and for history restore (called from
 * conversations.js when a stored chip message is encountered).
 *
 * chip: {chip_message_id, filename, fact_count, facts: [{key,value,confidence}], source_marker}
 * opts: {sessionId} — needed for the DELETE call
 */
export function renderFactIngestChip(chip, opts = {}) {
    if (!chip || !elements.chatContainer) return null;
    const sessionId = opts.sessionId || state.sessionId;

    const wrap = document.createElement('div');
    wrap.className = 'fact-ingest-chip';
    wrap.dataset.chipMessageId = chip.chip_message_id ?? '';
    wrap.dataset.sourceMarker = chip.source_marker || '';

    const facts = Array.isArray(chip.facts) ? chip.facts : [];
    const count = chip.fact_count ?? facts.length;
    const filename = chip.filename || 'document';

    // Compact header row (clickable to expand)
    const head = document.createElement('button');
    head.type = 'button';
    head.className = 'fact-ingest-head';
    head.innerHTML = `
        <svg class="fact-ingest-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
        </svg>
        <span class="fact-ingest-name"></span>
        <span class="fact-ingest-count"></span>
        <svg class="fact-ingest-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"/>
        </svg>
    `;
    head.querySelector('.fact-ingest-name').textContent = filename;
    head.querySelector('.fact-ingest-count').textContent =
        count === 1 ? '1 fact' : `${count} facts`;

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'fact-ingest-delete';
    del.title = 'Delete chip and its extracted facts';
    del.innerHTML = `
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
        </svg>
    `;

    // Fact list (collapsed by default)
    const body = document.createElement('div');
    body.className = 'fact-ingest-body';
    body.hidden = true;
    if (facts.length) {
        const ul = document.createElement('ul');
        ul.className = 'fact-ingest-list';
        for (const f of facts) {
            const li = document.createElement('li');
            const key = document.createElement('span');
            key.className = 'fact-ingest-key';
            key.textContent = f.key;
            const val = document.createElement('span');
            val.className = 'fact-ingest-value';
            val.textContent = f.value;
            li.appendChild(key);
            li.appendChild(document.createTextNode(': '));
            li.appendChild(val);
            ul.appendChild(li);
        }
        body.appendChild(ul);
    } else {
        const empty = document.createElement('div');
        empty.className = 'fact-ingest-empty';
        empty.textContent = 'No facts were extracted from this document.';
        body.appendChild(empty);
    }

    head.addEventListener('click', () => {
        const open = wrap.classList.toggle('open');
        body.hidden = !open;
    });

    del.addEventListener('click', async (e) => {
        e.stopPropagation();
        let ok = true;
        try {
            const dlg = await import('./confirm-dialog.js');
            ok = await dlg.confirm({
                title: 'Delete fact-ingest chip?',
                message: `Remove "${filename}" and its ${count} extracted ${count === 1 ? 'fact' : 'facts'} from this conversation? This can't be undone.`,
                confirmText: 'Delete',
                danger: true,
            });
        } catch {
            ok = window.confirm(`Delete ${filename} and its ${count} facts?`);
        }
        if (!ok) return;
        if (!sessionId || !chip.chip_message_id) {
            wrap.remove();
            return;
        }
        try {
            const { getHeaders: gh } = await import('./api.js');
            const res = await fetch(`/sessions/${sessionId}/fact-ingest/${chip.chip_message_id}`, {
                method: 'DELETE',
                headers: gh(false),
            });
            if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
        } catch (err) {
            console.error('[fact-ingest] delete failed:', err);
        }
        wrap.remove();
    });

    wrap.appendChild(head);
    wrap.appendChild(del);
    wrap.appendChild(body);
    elements.chatContainer.appendChild(wrap);
    if (!opts.noScroll) forceScrollToBottom();
    return wrap;
}

/**
 * The card left in the chat when a recording is ingested into memory.
 *
 * A sibling of the fact-ingest chip rather than a reuse of it. A recording is
 * not a document: it has a narrative, a transcript, a duration, speakers and
 * audio, and the operator who just ingested one expects to reach those from
 * here rather than going back to the recording manager to find the row again.
 */
export function renderRecordingIngestCard(card, opts = {}) {
    if (!card || !elements.chatContainer) return null;
    const sessionId = opts.sessionId || state.sessionId;
    const facts = Array.isArray(card.facts) ? card.facts : [];
    const count = card.fact_count ?? facts.length;
    const title = card.title || card.filename || 'recording';

    const wrap = document.createElement('div');
    wrap.className = 'fact-ingest-chip recording-ingest-chip';
    wrap.dataset.chipMessageId = card.chip_message_id ?? '';
    wrap.dataset.sourceMarker = card.source_marker || '';

    const head = document.createElement('button');
    head.type = 'button';
    head.className = 'fact-ingest-head';
    head.innerHTML = `
        <svg class="fact-ingest-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
        </svg>
        <span class="fact-ingest-name"></span>
        <span class="fact-ingest-count"></span>
        <svg class="fact-ingest-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"/>
        </svg>
    `;
    head.querySelector('.fact-ingest-name').textContent = title;

    // Say what it actually drew on. "12 facts" alone leaves the reader
    // guessing whether this came from the narrative or a raw transcript,
    // which is the difference between a good extraction and a poor one.
    const bits = [count === 1 ? '1 fact' : `${count} facts`];
    if (card.ingested_from) bits.push(`from the ${card.ingested_from}`);
    if (card.duration) {
        const m = Math.floor(card.duration / 60), s = Math.round(card.duration % 60);
        bits.push(`${m}:${String(s).padStart(2, '0')}`);
    }
    if (card.speaker_count) bits.push(`${card.speaker_count} speakers`);
    head.querySelector('.fact-ingest-count').textContent = bits.join(' · ');

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'fact-ingest-delete';
    del.title = 'Delete this card and the facts it added';
    del.innerHTML = `
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
        </svg>
    `;

    const body = document.createElement('div');
    body.className = 'fact-ingest-body';
    body.hidden = true;

    // The recording's own parts, reachable from here.
    const parts = document.createElement('div');
    parts.className = 'recording-ingest-parts';
    const partDefs = [
        ['transcript', 'Transcript', card.has_transcript],
        ['summary', 'Narrative', card.has_narrative],
        ['synopsis', 'Summary', card.has_synopsis],
    ];
    for (const [kind, label, present] of partDefs) {
        if (!present) continue;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'recording-ingest-part';
        b.textContent = label;
        b.addEventListener('click', async (e) => {
            e.stopPropagation();
            try {
                const mod = await import('./audio-recorder.js');
                await mod.openRecordingPart(card.filename, title, kind);
            } catch (err) {
                console.error('[recording-ingest] could not open', kind, err);
            }
        });
        parts.appendChild(b);
    }
    if (parts.children.length) body.appendChild(parts);

    if (facts.length) {
        const ul = document.createElement('ul');
        ul.className = 'fact-ingest-list';
        for (const f of facts) {
            const li = document.createElement('li');
            const key = document.createElement('span');
            key.className = 'fact-ingest-key';
            key.textContent = f.key;
            const val = document.createElement('span');
            val.className = 'fact-ingest-value';
            val.textContent = f.value;
            li.appendChild(key);
            li.appendChild(document.createTextNode(': '));
            li.appendChild(val);
            ul.appendChild(li);
        }
        body.appendChild(ul);
    } else {
        const empty = document.createElement('div');
        empty.className = 'fact-ingest-empty';
        empty.textContent = 'No facts were extracted from this recording.';
        body.appendChild(empty);
    }

    head.addEventListener('click', () => {
        const open = wrap.classList.toggle('open');
        body.hidden = !open;
    });

    del.addEventListener('click', async (e) => {
        e.stopPropagation();
        let ok = true;
        try {
            const dlg = await import('./confirm-dialog.js');
            ok = await dlg.confirm({
                title: 'Delete this recording card?',
                message: `Remove "${title}" and its ${count} extracted ${count === 1 ? 'fact' : 'facts'} from this conversation? The recording itself is not deleted.`,
                confirmText: 'Delete',
                danger: true,
            });
        } catch {
            ok = window.confirm(`Delete ${title} and its ${count} facts?`);
        }
        if (!ok) return;
        if (!sessionId || !card.chip_message_id) {
            wrap.remove();
            return;
        }
        try {
            // Same delete route as the document chip: both wrote their facts
            // under a source marker, and that endpoint deletes by marker.
            const { getHeaders: gh } = await import('./api.js');
            const res = await fetch(`/sessions/${sessionId}/fact-ingest/${card.chip_message_id}`, {
                method: 'DELETE',
                headers: gh(false),
            });
            if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
        } catch (err) {
            console.error('[recording-ingest] delete failed:', err);
        }
        wrap.remove();
    });

    wrap.appendChild(head);
    wrap.appendChild(del);
    wrap.appendChild(body);
    elements.chatContainer.appendChild(wrap);
    if (!opts.noScroll) forceScrollToBottom();
    return wrap;
}

/**
 * Update loading indicator text (for reasoning models)
 */
export function updateLoadingText(text) {
    const loader = document.getElementById('loading-indicator');
    if (loader) {
        const span = loader.querySelector('.loading-status');
        if (span) span.textContent = text;
    }
}

/**
 * Check if text contains thinking tags near the start
 */
export function hasThinkingContent(text) {
    const idx = text.indexOf('<think>');
    return idx !== -1 && idx < 20;
}

/**
 * Extract thinking content and response from model output
 * Returns { thinking: string|null, response: string, isComplete: boolean }
 */
export function separateThinkingContent(text) {
    const thinkStart = text.indexOf('<think>');
    const thinkEnd = text.indexOf('</think>');

    if (thinkStart === -1) {
        return { thinking: null, response: text, isComplete: true };
    }
    if (thinkEnd === -1) {
        const thinking = text.slice(thinkStart + 7);
        return { thinking, response: '', isComplete: false };
    }
    const thinking = text.slice(thinkStart + 7, thinkEnd).trim();
    const response = text.slice(thinkEnd + 8).trim();
    return { thinking, response, isComplete: true };
}

/**
 * Add or update thinking section in a message
 * Creates a collapsible section showing the model's reasoning process
 */
export function addThinkingToMessage(messageEl, thinkingContent, isComplete = false, durationMs = null) {
    let thinkingDiv = messageEl.querySelector('.thinking-section');
    let createdNow = false;

    if (!thinkingDiv) {
        createdNow = true;
        thinkingDiv = document.createElement('details');
        thinkingDiv.className = 'thinking-section';
        thinkingDiv.open = false; // Always collapsed — user can expand if they want
        thinkingDiv.dataset.startTime = Date.now();

        const summary = document.createElement('summary');
        summary.innerHTML = `
            <svg class="thinking-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <span class="thinking-label">Thinking</span>`;
        thinkingDiv.appendChild(summary);

        const content = document.createElement('div');
        content.className = 'thinking-content';
        thinkingDiv.appendChild(content);

        // Insert after header (if present), otherwise at beginning
        const header = messageEl.querySelector('.message-header');
        if (header) {
            header.after(thinkingDiv);
        } else {
            messageEl.insertBefore(thinkingDiv, messageEl.firstChild);
        }
    }

    // Update content (render markdown for richer thinking display)
    const contentDiv = thinkingDiv.querySelector('.thinking-content');
    if (contentDiv) {
        if (typeof marked !== 'undefined' && typeof DOMPurify !== 'undefined') {
            contentDiv.innerHTML = DOMPurify.sanitize(marked.parse(thinkingContent));
        } else {
            contentDiv.textContent = thinkingContent;
        }
    }

    // Update label when complete. Duration precedence:
    //   1. Server-measured (thinking_ms from the SSE completion payload) —
    //      the only number that's actually "how long the model thought".
    //   2. A previously frozen value — never recompute (the old code
    //      recomputed on every chunk, so the label crept up to the whole
    //      response time).
    //   3. Client wall-time from accordion creation — only meaningful when
    //      the accordion was created live (thinking streamed in); if it was
    //      created already-complete (history render, one-lump delivery),
    //      there's no timing to be had — label it honestly with no number.
    if (isComplete) {
        const label = thinkingDiv.querySelector('.thinking-label');
        if (label) {
            if (durationMs !== null && durationMs > 0) {
                thinkingDiv.dataset.thoughtMs = String(durationMs);
            } else if (!thinkingDiv.dataset.thoughtMs && !createdNow) {
                const startTime = parseInt(thinkingDiv.dataset.startTime || '0', 10);
                if (startTime) thinkingDiv.dataset.thoughtMs = String(Date.now() - startTime);
            }
            const ms = parseInt(thinkingDiv.dataset.thoughtMs || '0', 10);
            label.textContent = ms > 0 ? `Thought for ${Math.max(1, Math.round(ms / 1000))}s` : 'Thoughts';
        }
        thinkingDiv.open = false; // Collapse when thinking is done
    }

    return thinkingDiv;
}

/**
 * Hide loading indicator
 */
export function hideLoading() {
    const loader = document.getElementById('loading-indicator');
    if (loader) loader.remove();
}

/**
 * Clear chat container
 */
export function clearChat() {
    elements.chatContainer.innerHTML = '';
}

/**
 * Scroll chat to bottom (only if user is already at bottom - sticky scroll)
 * Use this during streaming to avoid interrupting user who scrolled up.
 */
export function scrollToBottom() {
    if (isUserAtBottom) {
        elements.chatContainer.scrollTop = elements.chatContainer.scrollHeight;
    }
}

/**
 * Force scroll to bottom and reset sticky state
 * Use this when adding new messages (user message, new assistant response starts)
 */
export function forceScrollToBottom() {
    isUserAtBottom = true;
    elements.chatContainer.scrollTop = elements.chatContainer.scrollHeight;
}

/**
 * Glide to the bottom instead of jumping.
 *
 * For hands-free use: in a spoken conversation the person is not holding
 * the scrollbar, so a reply that lands off-screen is simply missed. A
 * smooth scroll also shows WHICH WAY the page moved, which an instant jump
 * does not. Honours prefers-reduced-motion, and falls back to the instant
 * version on browsers without smooth behaviour.
 */
/**
 * Bring a newly added message into view.
 *
 * Glides while a voice session is open (hands-free: nobody is holding the
 * scrollbar, and a reply that lands off-screen is simply missed), snaps
 * otherwise — a smooth scroll on every token render would judder.
 */
export function scrollNewMessageIntoView() {
    if (document.body.classList.contains('voice-active')) smoothScrollToBottom();
    else forceScrollToBottom();
}

export function smoothScrollToBottom() {
    isUserAtBottom = true;
    const el = elements.chatContainer;
    if (!el) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof el.scrollTo !== 'function') {
        el.scrollTop = el.scrollHeight;
        return;
    }
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
}

/**
 * Set streaming state. Transforms the send button into a stop button while
 * a response is in flight (icon swap + `.stopping` class) rather than just
 * disabling it. The click handler in main.js inspects `.stopping` to decide
 * whether to call sendMessage or stopAll.
 */
export function setStreamingState(isStreaming) {
    if (elements.sendBtn) {
        elements.sendBtn.classList.toggle('stopping', !!isStreaming);
        // Keep the button enabled while streaming — it's now the stop button.
        // (We never set disabled=true here; cooldown UI still owns disabling
        // for its own reasons.)
        if (!isStreaming) elements.sendBtn.disabled = false;
    }
    // Disable narrator nav buttons during streaming so user can't advance mid-response
    const nextBtn = document.getElementById('voice-narrator-next');
    const prevBtn = document.getElementById('voice-narrator-prev');
    if (nextBtn) nextBtn.disabled = isStreaming;
    if (prevBtn) prevBtn.disabled = isStreaming;
}

// Cooldown UI state
let cooldownInterval = null;

/**
 * Start cooldown UI - disables send button and shows countdown
 * @param {number} durationMs - Cooldown duration in milliseconds
 */
export function startCooldownUI(durationMs) {
    if (!elements.sendBtn) return;

    const cooldownText = elements.sendBtn.querySelector('.send-cooldown-text');

    // Clear any existing interval
    if (cooldownInterval) {
        clearInterval(cooldownInterval);
    }

    const endTime = Date.now() + durationMs;

    const updateCooldownDisplay = () => {
        const remaining = Math.max(0, endTime - Date.now());
        if (remaining <= 0) {
            clearInterval(cooldownInterval);
            cooldownInterval = null;
            elements.sendBtn.classList.remove('cooldown');
            elements.sendBtn.disabled = false;
            if (cooldownText) cooldownText.textContent = '';
        } else {
            const secs = Math.ceil(remaining / 1000);
            elements.sendBtn.classList.add('cooldown');
            elements.sendBtn.disabled = true;
            if (cooldownText) cooldownText.textContent = `${secs}s`;
        }
    };

    updateCooldownDisplay();
    cooldownInterval = setInterval(updateCooldownDisplay, 1000);
}

/**
 * Clear cooldown UI (e.g., on successful send)
 */
export function clearCooldownUI() {
    if (cooldownInterval) {
        clearInterval(cooldownInterval);
        cooldownInterval = null;
    }
    if (elements.sendBtn) {
        elements.sendBtn.classList.remove('cooldown');
        const cooldownText = elements.sendBtn.querySelector('.send-cooldown-text');
        if (cooldownText) cooldownText.textContent = '';
        // Don't enable here - let normal streaming state control that
    }
}

/**
 * Focus message input
 */
export function focusInput() {
    elements.messageInput.focus();
}

/**
 * Get and clear message input value
 */
export function getAndClearInput() {
    const value = elements.messageInput.value.trim();
    elements.messageInput.value = '';
    return value;
}

/**
 * Add meta info to a message element
 */
export function updateMessageContent(messageEl, content) {
    const contentDiv = messageEl.querySelector('.message-content');
    if (contentDiv) {
        contentDiv.textContent = content;
    } else {
        // Fallback for messages without content wrapper
        messageEl.textContent = content;
    }
}

/**
 * Render message content as markdown (call on completion)
 * Uses marked.js for parsing and DOMPurify for XSS protection
 */
/**
 * Patch incomplete markdown tables so marked can render them mid-stream.
 * marked requires a separator row (|---|---|) after the header to recognise
 * a GFM table. During streaming the separator may not have arrived yet.
 * This scans for contiguous blocks of pipe-delimited lines and, if no
 * separator row exists in the block, injects one after the first row.
 */
function _patchIncompleteTable(text) {
    const lines = text.split('\n');
    const out = [];
    const SEP = /^\|(\s*:?-+:?\s*\|)+$/;
    const ROW = /^\|.+\|/;

    let i = 0;
    while (i < lines.length) {
        if (!ROW.test(lines[i].trim())) {
            out.push(lines[i]);
            i++;
            continue;
        }

        // Collect a contiguous block of pipe-rows
        const block = [];
        while (i < lines.length && ROW.test(lines[i].trim())) {
            block.push(lines[i]);
            i++;
        }

        // Check if any line in the block is a separator
        const hasSep = block.some(l => SEP.test(l.trim()));
        if (hasSep || block.length === 0) {
            out.push(...block);
        } else {
            // Inject separator after first row
            out.push(block[0]);
            const cols = block[0].split('|').length - 2;
            if (cols > 0) {
                out.push('|' + ' --- |'.repeat(cols));
            }
            out.push(...block.slice(1));
        }
    }
    return out.join('\n');
}

/**
 * Vertical space the writer actually left.
 *
 * Markdown flattens every run of blank lines into ONE paragraph break, so a
 * line set apart by three blank lines lands on the page identically to one
 * set apart by a single newline. That is the opposite of what was written,
 * and it no longer matches what is heard either: the speech side turns each
 * extra break into a longer pause (core/speech_pauses.py), so the page has to
 * show the same shape the ear is given.
 *
 * Runs past the first become an explicit spacer. Code fences are left alone —
 * blank lines inside a program are part of the program.
 */
const MAX_GAP_STEPS = 3;

function _keepBlankLines(text) {
    return String(text || '')
        .split(/(```[\s\S]*?```)/g)
        .map((part, i) => (i % 2 === 1 ? part : part.replace(/\n{3,}/g, (run) => {
            const steps = Math.min(MAX_GAP_STEPS, run.length - 2);
            return `\n\n<div class="md-gap md-gap-${steps}"></div>\n\n`;
        })))
        .join('');
}

// Strikethrough needs TWO tildes. GFM also strikes text between single
// tildes, and a report that writes "~$90K/year revenue (~$7,500/month)"
// (tilde = "about") came out with the revenue struck through and both
// tildes gone. The response editor already only honours "~~", so the two
// views disagreed about the same text. Returning undefined (not false)
// stops marked falling back to its own single-tilde rule.
if (typeof marked !== 'undefined' && marked.use) {
    marked.use({
        tokenizer: {
            del(src) {
                const m = /^~~(?=\S)([\s\S]*?\S)~~(?!~)/.exec(src);
                if (!m) return undefined;
                return { type: 'del', raw: m[0], text: m[1], tokens: this.lexer.inlineTokens(m[1]) };
            },
        },
    });
}

/** Assistant markdown as safe HTML: one path, so every view agrees. */
export function assistantMarkdownToHtml(content) {
    marked.setOptions({ gfm: true, breaks: true });
    return DOMPurify.sanitize(marked.parse(_keepBlankLines(_patchIncompleteTable(content))));
}

export function renderMessageMarkdown(messageEl, content) {
    const contentDiv = messageEl.querySelector('.message-content');
    if (!contentDiv) return;

    // Check if libraries are loaded
    if (typeof marked === 'undefined' || typeof DOMPurify === 'undefined') {
        contentDiv.textContent = content;
        return;
    }

    // Incomplete markdown tables are patched so they render during streaming
    // (marked needs a header + separator to see a table), and the writer's
    // blank lines are kept as real space. Both live in assistantMarkdownToHtml.
    contentDiv.innerHTML = assistantMarkdownToHtml(content);
    _addCodeCopyButtons(contentDiv);
    _makeGeneratedImagesClickable(contentDiv);
}

/**
 * Replace a dictation message's content with an editable textarea +
 * Save/Cancel buttons. On Save, PUT the new text to the server.
 */
function startDictationEdit(messageEl, messageId) {
    const contentEl = messageEl.querySelector('.message-content');
    const actionBar = messageEl.querySelector('.message-actions');
    if (!contentEl || !actionBar) return;
    const original = contentEl.textContent;

    // Lock the message footprint to its rendered size so editing doesn't
    // collapse it. We pin both width and height on the message element itself
    // (not just the textarea) since some parent layouts shrink-to-fit.
    const rect = contentEl.getBoundingClientRect();
    const renderedHeight = rect.height;
    const renderedWidth = rect.width;

    // Give the edit box the full available width instead of pinning it to
    // the rendered width. Dictation messages are flex items with
    // align-self:flex-start in a column flexbox, so a short note like
    // "Front room:" shrink-wraps to a tiny width — pinning to THAT locked
    // the editor narrow, so new dictated/typed content couldn't widen the
    // box (it just wrapped into a sliver). Pinning to the container's
    // content width (capped by .message max-width:85% in CSS) gives the
    // textarea room to grow horizontally; autoGrow handles vertical growth.
    const originalMessageWidth = messageEl.style.width;
    let editWidth = renderedWidth;
    const scroller = elements.chatContainer;
    if (scroller) {
        const cs = getComputedStyle(scroller);
        const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
        editWidth = scroller.clientWidth - padX;
    }
    messageEl.style.width = `${editWidth}px`;

    const textarea = document.createElement('textarea');
    textarea.className = 'dictation-edit-textarea';
    textarea.value = original;
    contentEl.replaceWith(textarea);
    textarea.style.height = `${Math.max(60, renderedHeight)}px`;
    textarea.focus();
    const autoGrow = () => {
        // Setting height to 'auto' forces a reflow with a momentarily
        // collapsed textarea; the browser reacts by scrolling the chat
        // container to keep the caret in view, which yanks the user to the
        // top of the dictation mid-typing (very visible with phone
        // voice-to-text firing rapid input events). Capture the scroll
        // position and restore it synchronously after the resize — before
        // the browser paints — so the view never jumps.
        const prevScroll = scroller ? scroller.scrollTop : 0;
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.max(renderedHeight, textarea.scrollHeight)}px`;
        if (scroller) scroller.scrollTop = prevScroll;
    };
    textarea.addEventListener('input', autoGrow);

    // Slide the bottom chat input bar away while editing so its send/dictation
    // buttons don't compete with the dictation's Save/Cancel. Removed in
    // restore() (covers save, cancel, and the save-error path).
    document.body.classList.add('dictation-editing');

    // Stash so restore() can clean up the pinned width
    textarea.dataset.originalMessageWidth = originalMessageWidth;

    // Save existing button HTML so we can restore on cancel
    const originalActionBarHtml = actionBar.innerHTML;
    actionBar.innerHTML = `
        <button class="message-action-btn dictation-cancel-btn">Cancel</button>
        <button class="message-action-btn dictation-save-btn ready">Save</button>
    `;

    const restore = (newText) => {
        // Bring the chat input bar back up.
        document.body.classList.remove('dictation-editing');
        const newContent = document.createElement('div');
        newContent.className = 'message-content';
        newContent.textContent = newText;
        textarea.replaceWith(newContent);
        // Undo the pinned width
        messageEl.style.width = textarea.dataset.originalMessageWidth || '';
        actionBar.innerHTML = originalActionBarHtml;
        // Re-wire the original buttons (TTS, Edit, Delete)
        actionBar.querySelector('.tts-btn')?.addEventListener('click', (e) => {
            const btn = e.currentTarget;
            const text = newContent.innerText.trim();
            if (text) {
                window.dispatchEvent(new CustomEvent('tts-play', {
                    detail: { text, messageEl, button: btn }
                }));
            }
        });
        actionBar.querySelector('.delete-btn')?.addEventListener('click', () => {
            if (window.showDeleteConfirmModal && messageId) {
                window.showDeleteConfirmModal(messageId, messageEl);
            }
        });
        actionBar.querySelector('.edit-btn')?.addEventListener('click', () => {
            startDictationEdit(messageEl, messageId);
        });
    };

    actionBar.querySelector('.dictation-cancel-btn').addEventListener('click', () => {
        restore(original);
    });
    actionBar.querySelector('.dictation-save-btn').addEventListener('click', async () => {
        const newText = textarea.value.trim();
        if (!newText || newText === original) {
            restore(original);
            return;
        }
        const { state } = await import('./state.js');
        const sessionId = state.sessionId;
        const headers = (await import('./api.js')).getHeaders(true);
        try {
            const res = await fetch(`/sessions/${sessionId}/messages/${messageId}/content`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({
                    content: newText,
                    reextract_facts: !!state.dictationExtractFacts,
                    model: state.loadedModel || null,
                }),
            });
            if (!res.ok) throw new Error(`save failed (${res.status})`);
            const data = await res.json().catch(() => ({}));
            restore(newText);
            // If re-extraction was scheduled, show spinner + poll for completion
            if (data.extracting) {
                const actionBarAfter = messageEl.querySelector('.message-actions');
                if (actionBarAfter && !actionBarAfter.querySelector('.extraction-status')) {
                    const spinner = document.createElement('div');
                    spinner.className = 'extraction-status';
                    spinner.innerHTML = '<div class="mini-spinner"></div><span>Extracting facts...</span>';
                    actionBarAfter.insertBefore(spinner, actionBarAfter.firstChild);
                }
                // Poll
                const { pollDictationExtractionStatus } = await import('./voice.js');
                if (pollDictationExtractionStatus) {
                    pollDictationExtractionStatus(sessionId, messageEl);
                }
            }
        } catch (e) {
            console.error('[Dictation] Edit save failed:', e);
            alert('Failed to save edit: ' + e.message);
            restore(original);
        }
    });
}

/**
 * Add copy buttons to <pre> code blocks inside a container.
 */
function _addCodeCopyButtons(container) {
    container.querySelectorAll('pre').forEach(pre => {
        if (pre.querySelector('.code-copy-btn')) return;
        pre.style.position = 'relative';
        const btn = document.createElement('button');
        btn.className = 'code-copy-btn';
        btn.textContent = 'Copy';
        btn.addEventListener('click', () => {
            const code = pre.querySelector('code');
            const text = (code || pre).textContent;
            navigator.clipboard.writeText(text).then(() => {
                btn.textContent = 'Copied!';
                setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
            });
        });
        pre.appendChild(btn);
    });
}

/**
 * Make generated images (src starting with /generated/) clickable to open gallery.
 * Clicking opens the image viewer with all generated images in the conversation.
 */
function _makeGeneratedImagesClickable(container) {
    container.querySelectorAll('img[src^="/generated/"]').forEach(img => {
        if (img.dataset.galleryBound) return;
        img.dataset.galleryBound = '1';
        img.style.cursor = 'pointer';
        img.addEventListener('click', () => {
            // Collect all generated images across the entire chat
            const chatContainer = document.getElementById('chat-container');
            if (!chatContainer) return;
            const allImgs = chatContainer.querySelectorAll('img[src^="/generated/"]');
            const images = [];
            let clickedIndex = 0;
            allImgs.forEach((el, i) => {
                images.push({
                    image: el.src,
                    thumbnail: el.src,
                    title: el.alt || 'Generated image',
                });
                if (el === img) clickedIndex = i;
            });
            if (images.length > 0) {
                openImageGallery(images, clickedIndex);
            }
        });
    });
}

/**
 * Show citation loading spinner on a message
 */
export function updateExtractionStatus(messageEl, messageId) {
    if (!messageEl) return;

    const actionBar = messageEl.querySelector('.message-actions');
    if (!actionBar) return;

    // Remove extraction status spinner
    const extractionStatus = actionBar.querySelector('.extraction-status');
    if (extractionStatus) {
        extractionStatus.remove();
    }

    // Set the message ID on the element
    messageEl.dataset.messageId = messageId;

    // Enable the delete button
    const deleteBtn = actionBar.querySelector('.delete-btn');
    if (deleteBtn) {
        deleteBtn.classList.add('ready');
        deleteBtn.addEventListener('click', () => {
            if (window.showDeleteConfirmModal) {
                window.showDeleteConfirmModal(messageId, messageEl);
            }
        });
    }

    // Enable the redo button
    const redoBtn = actionBar.querySelector('.redo-btn');
    if (redoBtn) {
        redoBtn.classList.add('ready');
        redoBtn.addEventListener('click', () => {
            if (window.showRedoModal) {
                window.showRedoModal(messageId, messageEl);
            }
        });
    }

    // Enable the edit button now that the message exists to be edited
    const msgEditBtn = actionBar.querySelector('.msg-edit-btn');
    if (msgEditBtn) {
        msgEditBtn.classList.add('ready');
        msgEditBtn.addEventListener('click', () => {
            window.openMessageEditor?.(messageEl, messageId);
        });
    }
    actionBar.querySelector('.msg-copy-btn')?.classList.add('ready');
    actionBar.querySelector('.msg-export-btn')?.classList.add('ready');
    wireCopyAndExport(actionBar, messageEl, messageId);

    // Update narrator mode delete restrictions (this message may disable others)
    updateNarratorDeleteButtons();
}

// ============================================================================
// Narrator Mode Delete Restrictions
// ============================================================================

// SVG for narrator delete button (trash can with revert arrow)
const NARRATOR_DELETE_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
    <path d="M14.5 10l-3 3 3 3" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="11.5" y1="13" x2="17" y2="13" stroke-linecap="round"/>
</svg>`;

// Standard delete SVG (for restoring when leaving narrator mode)
const STANDARD_DELETE_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
</svg>`;

/**
 * Update delete/redo buttons for narrator mode.
 * In narrator mode:
 *  - Only the most recent assistant message can be deleted
 *  - Delete icon shows a revert arrow (trash + left arrow)
 *  - Redo button hidden when verbatim is on (no LLM to regenerate)
 */
export async function updateNarratorDeleteButtons() {
    // Dynamically import state to avoid circular dependencies
    const { state } = await import('./state.js');

    const assistantMessages = elements.chatContainer?.querySelectorAll('.message.assistant');
    if (!assistantMessages || assistantMessages.length === 0) return;

    const isVerbatim = state.narratorMode && state.agentSettings?.narrator_verbatim;

    // Convert to array and find the last one
    const messagesArray = Array.from(assistantMessages);
    const lastMessage = messagesArray[messagesArray.length - 1];

    messagesArray.forEach(msg => {
        const deleteBtn = msg.querySelector('.delete-btn');
        const redoBtn = msg.querySelector('.redo-btn');

        if (state.narratorMode) {
            if (msg === lastMessage) {
                // Last message: enable delete with narrator revert icon
                if (deleteBtn) {
                    deleteBtn.classList.remove('narrator-disabled');
                    deleteBtn.innerHTML = NARRATOR_DELETE_SVG;
                    deleteBtn.title = 'Delete and revert to this chunk';
                }
                // Hide redo in verbatim mode (nothing to regenerate)
                if (redoBtn) {
                    if (isVerbatim) {
                        redoBtn.classList.add('hidden');
                    } else {
                        redoBtn.classList.remove('hidden');
                    }
                }
            } else {
                // Older messages: disable delete, hide redo
                if (deleteBtn) {
                    deleteBtn.classList.add('narrator-disabled');
                    deleteBtn.innerHTML = STANDARD_DELETE_SVG;
                    deleteBtn.title = 'Narrator mode: delete most recent response first';
                }
            }
        } else {
            // Not in narrator mode: restore standard icons and enable all
            if (deleteBtn) {
                deleteBtn.classList.remove('narrator-disabled');
                deleteBtn.innerHTML = STANDARD_DELETE_SVG;
                deleteBtn.title = 'Delete this response';
            }
            if (redoBtn) {
                redoBtn.classList.remove('hidden');
            }
        }
    });
}

// Expose globally for external calls
window.updateNarratorDeleteButtons = updateNarratorDeleteButtons;

// ============================================================================
// Delete Confirmation Modal
// ============================================================================

// Re-export modal entry points (moved to ui-modals.js) for external importers.
export { initDeleteModal, showDeleteConfirmModal, initRedoModal, showRedoModal };

// Re-export loading-step renderers (moved to ui-steps.js) for external importers.
export { addLoadingStep, updateStepSubstep, updateLoadingStep, solidifyLoadingSteps, renderDataBlocks, showStepAccordion };

// Re-export message-annotation renderers (moved to ui-message-extras.js).
export { addMetaToMessage, addAnalyticsToMessage, showCitationLoading, updateCitations, addHeaderToMessage, showRoutingMatches, showToolsUsed, showImageSearchResults };
